import React, { useState } from 'react';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { clientsSelector } from '../../store/slices/clients';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import {
  API_NEWCLIENT,
  AWS_BUCKET_CLIENT_LOGOS,
  S3_GET_PRESIGNED_URLS,
  HOLIDAYS_LIST
} from '../../utils/constants';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription
} from '../../components/ui/card';
import {
  Search,
  Save,
  Building2,
  ImageIcon,
  Briefcase,
  Clock,
  MapPin,
  MapPinned,
  Globe,
  Mail,
  Share2,
  Tag,
  Settings,
  Sparkles,
  ToggleLeft,
  Calendar
} from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import TagsInput from './TagsInput';
import BusinessHours from './BusinessHours/BusinessHours';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog.jsx';

import { selectedClientSelector } from '../../store/slices/selectedClient';

const getHolidaysFromEnv = () => {
  const envHolidays = HOLIDAYS_LIST;
  if (!envHolidays) return [];
  try {
    if (envHolidays.trim().startsWith('[')) {
      return JSON.parse(envHolidays);
    }
    return envHolidays
      .split(',')
      .map((h) => h.trim())
      .filter(Boolean);
  } catch {
    return [];
  }
};

const NewClient = () => {
  const selectedClientState = useSelector(selectedClientSelector);
  const selectedClient = selectedClientState?.selectedClient;

  // Initialize empty state for new client
  const [businessHours, setBusinessHours] = useState({
    monday: '9/18',
    tuesday: '9/18',
    wednesday: '9/18',
    thursday: '9/18',
    friday: '9/18',
    saturday: '/',
    sunday: '/'
  });
  const errors = { businessHours: {} };
  const handleBusinessHoursChange = (field, value) => {
    setBusinessHours((prev) => ({ ...prev, [field]: value }));
  };
  const history = useHistory();
  const { customerList } = useSelector(clientsSelector);
  const [searchTerm, setSearchTerm] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Social Networks toggles
  const [noFacebook, setNoFacebook] = useState(false);
  const [noAIImages, setNoAIImages] = useState(false);
  const [noGoogleMyBusiness, setNoGoogleMyBusiness] = useState(false);
  const [noInstagram, setNoInstagram] = useState(false);

  // Content Strategy tags
  const [holidays, setHolidays] = useState([]);
  const [instagramTags, setInstagramTags] = useState([]);
  const [facebookTags, setFacebookTags] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [negativeKeywords, setNegativeKeywords] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);

  // Reviews Settings
  const [automaticReviews, setAutomaticReviews] = useState(
    selectedClient?.automaticReviews ?? true
  );
  const [singlePersonBusiness, setSinglePersonBusiness] = useState(false);
  const [noReviews, setNoReviews] = useState(false);
  // User Management
  const [fullAccess, setFullAccess] = useState(false);

  // Basic business info states - empty for new client
  const [businessName, setBusinessName] = useState('');
  const [phone, setPhone] = useState('');
  const [secondaryPhone, setSecondaryPhone] = useState('');
  const [listingPhone, setListingPhone] = useState('primary');
  const [industry, setIndustry] = useState('');
  const [timezone, setTimezone] = useState('');
  const [address, setAddress] = useState('');
  const [serviceArea, setServiceArea] = useState([]);
  const [website, setWebsite] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [primaryServices, setPrimaryServices] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});

  const customMultiSelectorStyle = {
    control: (provided) => ({
      ...provided,
      minHeight: '56px',
      borderRadius: '0.75rem',
      borderWidth: '2px',
      borderColor: 'hsl(var(--border))',
      backgroundColor: 'hsl(var(--background))',
      boxShadow: 'none',
      '&:hover': {
        borderColor: 'hsl(var(--primary))'
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0 12px'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'hsl(var(--primary))',
      borderRadius: '8px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'hsl(var(--primary-foreground))',
      fontWeight: 500
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'hsl(var(--primary-foreground))',
      ':hover': {
        backgroundColor: 'hsl(var(--primary))',
        color: 'hsl(var(--primary-foreground))'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: () => ({
      display: 'none'
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 30
    })
  };

  // Filtrar clientes por nombre
  const filteredClients = customerList.filter((c) =>
    c.clientName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const attributeList = [
    { value: 1, label: 'Curbside Pickup' },
    { value: 2, label: 'Deliver' },
    { value: 3, label: 'Dine-in' },
    { value: 4, label: 'Drive through' },
    { value: 5, label: 'No-contact delivery' },
    { value: 6, label: 'Outdoor seating' },
    { value: 7, label: 'Takeout' },
    { value: 8, label: 'Assistive hearing loop' },
    { value: 9, label: 'Beach wheelchairs' },
    { value: 10, label: 'Mobility scooter rental' },
    { value: 11, label: 'Passenger loading area' },
    { value: 12, label: 'Wheelchair-accessible elevator' },
    { value: 13, label: 'Wheelchair-accessible entrance' },
    { value: 14, label: 'Wheelchair-accessible parking' },
    { value: 15, label: 'Wheelchair-accessible restroom' },
    { value: 16, label: 'Wheelchair-accessible seating' },
    { value: 17, label: 'Adults only' },
    { value: 18, label: 'Family-friendly' },
    { value: 19, label: 'Kid-friendly' },
    { value: 20, label: 'LGBTQ+ friendly' },
    { value: 21, label: 'Transgender safespace' },
    { value: 22, label: 'Accepting new patients' },
    { value: 23, label: 'Accepts reservations' },
    { value: 24, label: 'Appointment required' },
    { value: 25, label: 'Membership required' },
    { value: 26, label: 'Online events calendar' },
    { value: 27, label: 'Reservations recommended' },
    { value: 28, label: 'Reservations required' },
    { value: 29, label: 'Beach access' },
    { value: 30, label: 'Bicycles for rent' },
    { value: 31, label: 'Boats for rent' },
    { value: 32, label: 'Game room' },
    { value: 33, label: 'Horseback riding' },
    { value: 34, label: 'Jogging track' },
    { value: 35, label: 'Nightclub' },
    { value: 36, label: 'Scuba diving' },
    { value: 37, label: 'Snorkeling' },
    { value: 38, label: 'Tennis' },
    { value: 39, label: 'Water skiing' },
    { value: 40, label: 'Identifies as black owned' },
    { value: 41, label: 'Identifies as veteran-led' },
    { value: 42, label: 'Identifies as women-led' },
    { value: 43, label: 'Cash Only' },
    { value: 44, label: 'Checks' },
    { value: 45, label: 'Debit Cards' },
    { value: 46, label: 'NFC Mobile Payments' },
    { value: 47, label: 'American Express' },
    { value: 48, label: 'Master Card' },
    { value: 49, label: 'Visa' },
    { value: 50, label: 'Discover' },
    { value: 51, label: 'Diners club' }
  ];

  const uploadLogoToS3WithClientId = async (file, clientId) => {
    const fileType = file.type;
    const fileExtension = file.name.split('.').pop();
    // Usar el clientId como prefijo: clientId/logo.ext
    const fileName = `${clientId}/logo.${fileExtension}`;

    try {
      // 1. Obtener la URL prefirmada desde tu API Gateway
      const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketName: AWS_BUCKET_CLIENT_LOGOS,
          files: [{ fileName, fileType }]
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get presigned URL');
      }

      const { urls } = await presignedResponse.json();
      const presignedUrl = urls?.[0]?.url;

      if (!presignedUrl) {
        throw new Error('No presigned URL returned');
      }

      // 2. Subir el archivo directamente a S3
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': fileType
        },
        body: file
      });

      if (!uploadResult.ok) {
        throw new Error('File upload to S3 failed');
      }

      // 3. Retornar la URL del logo subido con timestamp para forzar recarga de caché
      const uploadedFileUrl = `https://${AWS_BUCKET_CLIENT_LOGOS}.s3.amazonaws.com/${fileName}?v=${Date.now()}`;
      console.log(`Logo uploaded successfully: ${uploadedFileUrl}`);
      return uploadedFileUrl;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Guardar el archivo para subirlo después
      setLogoFile(file);

      // Mostrar preview local
      const reader = new window.FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
  };

  const handleSaveClient = async () => {
    // Validate required fields
    const newErrors = {};
    const newTouched = {
      businessName: true,
      phone: true,
      industry: true,
      timezone: true,
      serviceArea: true
    };

    if (!businessName || businessName.trim() === '') {
      newErrors.businessName = 'Business name is required';
    }

    if (!phone || phone.trim() === '') {
      newErrors.phone = 'Phone number is required';
    }

    if (!industry || industry.trim() === '') {
      newErrors.industry = 'Industry is required';
    }

    if (!timezone || timezone.trim() === '') {
      newErrors.timezone = 'Time zone is required';
    }

    if (!serviceArea || serviceArea.length === 0) {
      newErrors.serviceArea = 'Primary Service Area is required';
    }

    setValidationErrors(newErrors);
    setTouched(newTouched);

    // If there are validation errors, stop here
    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Build client flags array
      const clientFlags = [];
      if (noFacebook) clientFlags.push('NO_FB');
      if (noInstagram) clientFlags.push('NO_IG');
      if (noGoogleMyBusiness) clientFlags.push('NO_GMB');
      if (noAIImages) clientFlags.push('NO_AI');
      if (noReviews) clientFlags.push('NO_REVIEWS');

      // Build merge fields
      const mergeFields = {
        '~Address': address,
        '~Phone': listingPhone === 'primary' ? phone : secondaryPhone,
        '~Website': website,
        '~Email': email
      };

      // Prepare client data
      let clientName = businessName.replace(/\s+/g, ' ').trim();
      clientName = clientName.replace(/'/g, "'");

      const clientData = {
        clientName,
        timeZone: timezone,
        serviceArea,
        description,
        businessHours,
        mergeFields,
        industry,
        primaryService: primaryServices,
        additionalService: additionalServices,
        attributes,
        promotions,
        igTags: instagramTags,
        fbTags: facebookTags,
        negativeKeywords,
        holidays,
        clientFlags,
        automaticReviews,
        singlePerson: singlePersonBusiness
        // No logoUrl here - will be added after upload
      };

      // 1. Crear el cliente primero
      const response = await axios({
        method: 'post',
        url: API_NEWCLIENT,
        data: clientData
      });

      if (response.status === 200 && response.data) {
        const { id: clientId } = response.data;

        // 2. Si hay logo, subirlo a S3 con el ID del cliente
        if (logoFile && clientId) {
          try {
            setUploadingLogo(true);
            const logoUrl = await uploadLogoToS3WithClientId(
              logoFile,
              clientId
            );

            // 3. Actualizar el cliente con todos los datos + la URL del logo
            await axios({
              method: 'post',
              url: API_NEWCLIENT,
              data: {
                ...clientData,
                id: clientId,
                logoUrl
              }
            });

            console.log('Client created and logo uploaded successfully');
          } catch (logoError) {
            console.error('Error uploading logo:', logoError);
            alert(
              'Client created but logo upload failed. You can upload it later from the client settings.'
            );
          } finally {
            setUploadingLogo(false);
          }
        }

        setSuccessModalOpen(true);
      }
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Error saving client. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex-1 transition-all duration-300">
        <main className="p-4 sm:p-6 lg:p-10 space-y-6 sm:space-y-8 overflow-x-hidden">
          {' '}
          {/* Header y búsqueda */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                New Client
              </h1>
              <p className="text-base text-muted-foreground mt-1">
                Create a new client profile
              </p>
            </div>
            <div className="flex items-center gap-4 w-full sm:w-auto">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3 w-full sm:w-auto"
                onClick={handleSaveClient}
                disabled={loading || uploadingLogo}>
                <Save className="h-5 w-5" />
                {uploadingLogo
                  ? 'Uploading Logo...'
                  : loading
                  ? 'Saving...'
                  : 'Save Client'}
              </Button>
            </div>
          </div>
          {/* Business Information */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Business Details
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Enter the client's business information
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8 px-4 sm:px-6 lg:px-8 pb-8">
              {/* Business Name */}
              <div className="space-y-3">
                <Label
                  htmlFor="businessName"
                  className="text-base font-semibold flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  Business Name
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  The official name of the business as it appears on listings
                </p>
                <Input
                  id="businessName"
                  placeholder="Enter business name"
                  className={`h-14 text-lg rounded-xl border-2 px-4 ${
                    touched.businessName && validationErrors.businessName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-border focus:border-primary'
                  }`}
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                />
                {touched.businessName && validationErrors.businessName && (
                  <p className="text-red-500 text-sm">
                    {validationErrors.businessName}
                  </p>
                )}
              </div>

              {/* Business Logo */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  Business Logo
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  Upload the company logo to display across listings
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  {logoPreview ? (
                    <div className="relative">
                      <div className="h-20 w-20 rounded-xl border-2 border-border overflow-hidden bg-muted">
                        <img
                          src={logoPreview}
                          alt="Business logo"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <button
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center shadow-md hover:bg-destructive/90 transition-colors">
                        <svg
                          className="h-3.5 w-3.5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="h-20 w-20 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/50 flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                    </div>
                  )}
                  <div>
                    <label
                      htmlFor="logo-upload-new"
                      className="gap-2 h-12 px-6 rounded-xl text-base border border-border bg-background transition-colors flex items-center inline-flex cursor-pointer hover:bg-muted">
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    </label>
                    <input
                      id="logo-upload-new"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      PNG, JPG or SVG. Max 2MB. Will be uploaded when you
                      save.tumadre
                    </p>
                  </div>
                </div>
              </div>
              {/* ...existing code... (Phone, Industry, Address, Service Area, Website, Email, Description) */}
              {/* Phone Numbers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label
                    htmlFor="phone"
                    className="text-base font-semibold flex items-center gap-2">
                    <svg
                      className="h-5 w-5 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      viewBox="0 0 24 24">
                      <path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.13.37 2.25.72 3.32a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c1.07.35 2.19.59 3.32.72A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Phone Number
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Main business phone number
                  </p>
                  <Input
                    id="phone"
                    placeholder="(000) 000-0000"
                    className={`h-14 text-lg rounded-xl border-2 px-4 ${
                      touched.phone && validationErrors.phone
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-border focus:border-primary'
                    }`}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  {touched.phone && validationErrors.phone && (
                    <p className="text-red-500 text-sm">
                      {validationErrors.phone}
                    </p>
                  )}
                  <div className="flex items-center space-x-3 pt-1">
                    <input
                      type="radio"
                      name="listingPhone"
                      value="primary"
                      id="primaryListing"
                      className="h-5 w-5"
                      checked={listingPhone === 'primary'}
                      onChange={(e) => setListingPhone(e.target.value)}
                    />
                    <label
                      htmlFor="primaryListing"
                      className="text-base text-muted-foreground cursor-pointer">
                      Use as my listing phone
                    </label>
                  </div>
                </div>
                <div className="space-y-3">
                  <Label
                    htmlFor="secondaryPhone"
                    className="text-base font-semibold flex items-center gap-2">
                    <svg
                      className="h-5 w-5 text-muted-foreground"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      viewBox="0 0 24 24">
                      <path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.13.37 2.25.72 3.32a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c1.07.35 2.19.59 3.32.72A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    Secondary Phone
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    An alternate number customers can reach
                  </p>
                  <Input
                    id="secondaryPhone"
                    placeholder="(000) 000-0000"
                    className="h-14 text-lg rounded-xl border-2 focus:border-primary px-4"
                    value={secondaryPhone}
                    onChange={(e) => setSecondaryPhone(e.target.value)}
                  />
                  <div className="flex items-center space-x-3 pt-1">
                    <input
                      type="radio"
                      name="listingPhone"
                      value="secondary"
                      id="secondaryListing"
                      className="h-5 w-5"
                      checked={listingPhone === 'secondary'}
                      onChange={(e) => setListingPhone(e.target.value)}
                    />
                    <label
                      htmlFor="secondaryListing"
                      className="text-base text-muted-foreground cursor-pointer">
                      Use as my listing phone
                    </label>
                  </div>
                </div>
              </div>
              {/* Industry & Time Zone */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label
                    htmlFor="industry"
                    className="text-base font-semibold flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-muted-foreground" />
                    Industry
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Select the category that best describes the business
                  </p>
                  <Select onValueChange={setIndustry} value={industry}>
                    <SelectTrigger
                      id="industry"
                      className={`h-14 text-lg rounded-xl border-2 px-4 bg-background w-full ${
                        touched.industry && validationErrors.industry
                          ? 'border-red-500 focus:border-red-500'
                          : 'focus:border-primary border-border'
                      }`}>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent className="bg-card z-50 max-h-80 rounded-xl">
                      <SelectGroup>
                        <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                          Home Services
                        </SelectLabel>
                        <SelectItem value="plumber">Plumber</SelectItem>
                        <SelectItem value="electrician">Electrician</SelectItem>
                        <SelectItem value="hvac">HVAC</SelectItem>
                        <SelectItem value="general-contractor">
                          General Contractor
                        </SelectItem>
                        <SelectItem value="roofer">Roofer</SelectItem>
                        <SelectItem value="painter">Painter</SelectItem>
                        <SelectItem value="landscaping">
                          Landscaping / Lawn Care
                        </SelectItem>
                        <SelectItem value="cleaning-services">
                          Cleaning Services
                        </SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                          Food & Hospitality
                        </SelectLabel>
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="cafe">Cafe / Coffee Shop</SelectItem>
                        <SelectItem value="bakery">Bakery</SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                          Health & Medical
                        </SelectLabel>
                        <SelectItem value="dentist">Dentist</SelectItem>
                        <SelectItem value="doctor">Doctor / Clinic</SelectItem>
                        <SelectItem value="chiropractor">
                          Chiropractor
                        </SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                          Personal Care & Wellness
                        </SelectLabel>
                        <SelectItem value="hair-salon">Hair Salon</SelectItem>
                        <SelectItem value="barber">Barber</SelectItem>
                        <SelectItem value="spa">Spa / Massage</SelectItem>
                        <SelectItem value="gym">Fitness / Gym</SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                          Automotive
                        </SelectLabel>
                        <SelectItem value="auto-repair">Auto Repair</SelectItem>
                        <SelectItem value="car-wash">Car Wash</SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs font-bold text-primary uppercase tracking-wider py-2 px-3 bg-muted/50">
                          Other
                        </SelectLabel>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {touched.industry && validationErrors.industry && (
                    <p className="text-red-500 text-sm">
                      {validationErrors.industry}
                    </p>
                  )}
                </div>
                <div className="space-y-3">
                  <Label
                    htmlFor="timezone"
                    className="text-base font-semibold flex items-center gap-2">
                    <Clock className="h-5 w-5 text-muted-foreground" />
                    Time Zone
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Business operating time zone
                  </p>
                  <Select onValueChange={setTimezone} value={timezone}>
                    <SelectTrigger
                      id="timezone"
                      className={`h-14 text-lg rounded-xl border-2 px-4 bg-background w-full ${
                        touched.timezone && validationErrors.timezone
                          ? 'border-red-500 focus:border-red-500'
                          : 'focus:border-primary border-border'
                      }`}>
                      <SelectValue placeholder="Select time zone" />
                    </SelectTrigger>
                    <SelectContent className="bg-card z-50 rounded-xl">
                      <SelectItem value="america-new_york">
                        Eastern Time (ET)
                      </SelectItem>
                      <SelectItem value="america-chicago">
                        Central Time (CT)
                      </SelectItem>
                      <SelectItem value="america-denver">
                        Mountain Time (MT)
                      </SelectItem>
                      <SelectItem value="america-los_angeles">
                        Pacific Time (PT)
                      </SelectItem>
                      <SelectItem value="america-anchorage">
                        Alaska Time (AKT)
                      </SelectItem>
                      <SelectItem value="pacific-honolulu">
                        Hawaii Time (HT)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {touched.timezone && validationErrors.timezone && (
                    <p className="text-red-500 text-sm">
                      {validationErrors.timezone}
                    </p>
                  )}
                </div>
              </div>
              {/* Address */}
              <div className="space-y-3">
                <Label
                  htmlFor="address"
                  className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  Business Address
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  Physical business location
                </p>
                <Input
                  id="address"
                  placeholder="Enter business address"
                  className="h-14 text-lg rounded-xl border-2 focus:border-primary px-4"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              {/* Service Area */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <MapPinned className="h-5 w-5 text-muted-foreground" />
                  Service Area
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  Which cities or areas does this business serve?
                </p>
                <CreatableSelect
                  id="serviceArea"
                  name="serviceArea"
                  isMulti
                  isClearable={false}
                  value={serviceArea.map((area, index) => ({
                    value: index,
                    label: area
                  }))}
                  noOptionsMessage={() => 'Type a city and press Enter...'}
                  onChange={(selectedOption) => {
                    setServiceArea(
                      selectedOption
                        ? selectedOption.map((area) => area.label)
                        : []
                    );
                  }}
                  options={[]}
                  placeholder="Type a city and press Enter..."
                  styles={{
                    ...customMultiSelectorStyle,
                    control: (provided, state) => ({
                      ...customMultiSelectorStyle.control(provided, state),
                      borderColor:
                        touched.serviceArea && validationErrors.serviceArea
                          ? '#ef4444'
                          : 'hsl(var(--border))',
                      boxShadow:
                        touched.serviceArea && validationErrors.serviceArea
                          ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                          : 'none',
                      '&:hover': {
                        borderColor:
                          touched.serviceArea && validationErrors.serviceArea
                            ? '#ef4444'
                            : 'hsl(var(--primary))'
                      }
                    })
                  }}
                />
                {touched.serviceArea && validationErrors.serviceArea && (
                  <p className="text-red-500 text-sm">
                    {validationErrors.serviceArea}
                  </p>
                )}
              </div>
              {/* Website */}
              <div className="space-y-3">
                <Label
                  htmlFor="website"
                  className="text-base font-semibold flex items-center gap-2">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  Website
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  Business website URL
                </p>
                <Input
                  id="website"
                  placeholder="https://"
                  className="h-14 text-lg rounded-xl border-2 focus:border-primary px-4"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              {/* Email */}
              <div className="space-y-3">
                <Label
                  htmlFor="email"
                  className="text-base font-semibold flex items-center gap-2">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  Email
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  Business email address for customer inquiries
                </p>
                <Input
                  id="email"
                  type="email"
                  placeholder="contact@example.com"
                  className="h-14 text-lg rounded-xl border-2 focus:border-primary px-4"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              {/* Business Description */}
              <div className="space-y-3">
                <Label
                  htmlFor="description"
                  className="text-base font-semibold">
                  Business Description
                </Label>
                <p className="text-sm text-muted-foreground -mt-1">
                  Tell customers what makes this business special
                </p>
                <Textarea
                  id="description"
                  className="min-h-[140px] text-lg rounded-xl border-2 focus:border-primary p-4 resize-none placeholder:text-sm"
                  placeholder="Describe the business..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              {/* For brevity, you can copy the rest of the sections from lovablenewclient.tsx as needed. */}
            </CardContent>
          </Card>
          {/* Business Hours */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Business Hours
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    When is this business open?
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 lg:px-8 pb-8">
              <BusinessHours
                values={{ businessHours }}
                setFieldValue={(field, value) =>
                  handleBusinessHoursChange(field.split('.')[1], value)
                }
                errors={errors}
                touched={touched}
                onBlur={() => {}}
              />
            </CardContent>
          </Card>
          {/* Services */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Services Offered
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    What services does this business provide?
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6 lg:px-8 pb-8">
              <CreatableSelect
                id="services"
                name="services"
                isMulti
                isClearable={false}
                value={primaryServices.map((service, index) => ({
                  value: index,
                  label: service
                }))}
                noOptionsMessage={() => 'Type a service and press Enter...'}
                onChange={(selectedOption) => {
                  setPrimaryServices(
                    selectedOption
                      ? selectedOption.map((service) => service.label)
                      : []
                  );
                }}
                options={[]}
                placeholder="Type a service and press Enter..."
                styles={customMultiSelectorStyle}
              />
            </CardContent>
          </Card>
          {/* Social Networks Card */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Share2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Social Networks
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Configure which social platforms to post on
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 px-4 sm:px-6 lg:px-8 pb-8">
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">
                    Disable Facebook Posting
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Disable Facebook posting for this client
                  </p>
                </div>
                <Switch
                  checked={noFacebook}
                  onCheckedChange={setNoFacebook}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">
                    Disable Instagram Posting
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Disable Instagram posting for this client
                  </p>
                </div>
                <Switch
                  checked={noInstagram}
                  onCheckedChange={setNoInstagram}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">
                    Disable Google Business Posting
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Disable Google My Business posting
                  </p>
                </div>
                <Switch
                  checked={noGoogleMyBusiness}
                  onCheckedChange={setNoGoogleMyBusiness}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
              <div className="flex items-center justify-between py-4">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">
                    Disable AI-Generated Images
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Disable AI-generated images for this account
                  </p>
                </div>
                <Switch
                  checked={noAIImages}
                  onCheckedChange={setNoAIImages}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
            </CardContent>
          </Card>
          {/* Content Strategy Card */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Tag className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Content Tags & Preferences
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Configure content preferences and tags
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8 px-4 sm:px-6 lg:px-8 pb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Settings className="h-5 w-5 text-muted-foreground" />
                    Attributes
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Select attributes that describe the content style
                  </p>
                  <ReactSelect
                    id="attributes"
                    className="basic-multi-select"
                    options={attributeList}
                    value={attributeList.filter((attr) =>
                      attributes.includes(attr.label)
                    )}
                    onChange={(selectedOptions) => {
                      setAttributes(
                        selectedOptions
                          ? selectedOptions.map((attr) => attr.label)
                          : []
                      );
                    }}
                    isMulti
                    isClearable={false}
                    closeMenuOnSelect={false}
                    placeholder="Select attributes"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Tag className="h-5 w-5 text-muted-foreground" />
                    Instagram Tags
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Tags to include in Instagram posts
                  </p>
                  <CreatableSelect
                    id="igTags"
                    name="igTags"
                    isMulti
                    isClearable={false}
                    value={instagramTags.map((tag, index) => ({
                      value: index,
                      label: tag
                    }))}
                    noOptionsMessage={() => 'Type an Instagram tag'}
                    onChange={(selectedOption) => {
                      setInstagramTags(
                        selectedOption
                          ? selectedOption.map((tag) => tag.label)
                          : []
                      );
                    }}
                    options={[]}
                    placeholder="Type an Instagram tag"
                    styles={customMultiSelectorStyle}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-muted-foreground" />
                    Promotions
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Current promotions to highlight in content
                  </p>
                  <CreatableSelect
                    id="promotions"
                    name="promotions"
                    isMulti
                    isClearable={false}
                    value={promotions.map((promotion, index) => ({
                      value: index,
                      label: promotion
                    }))}
                    noOptionsMessage={() => 'Type a promotion'}
                    onChange={(selectedOption) => {
                      setPromotions(
                        selectedOption
                          ? selectedOption.map((promotion) => promotion.label)
                          : []
                      );
                    }}
                    options={[]}
                    placeholder="Type a promotion"
                    styles={customMultiSelectorStyle}
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Tag className="h-5 w-5 text-muted-foreground" />
                    Facebook Tags
                  </Label>
                  <p className="text-sm text-muted-foreground -mt-1">
                    Tags to include in Facebook posts
                  </p>
                  <CreatableSelect
                    id="fbTags"
                    name="fbTags"
                    isMulti
                    isClearable={false}
                    value={facebookTags.map((tag, index) => ({
                      value: index,
                      label: tag
                    }))}
                    noOptionsMessage={() => 'Type a Facebook tag'}
                    onChange={(selectedOption) => {
                      setFacebookTags(
                        selectedOption
                          ? selectedOption.map((tag) => tag.label)
                          : []
                      );
                    }}
                    options={[]}
                    placeholder="Type a Facebook tag"
                    styles={customMultiSelectorStyle}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    Negative Keywords
                  </Label>
                  <CreatableSelect
                    id="negativeKeywords"
                    name="negativeKeywords"
                    isMulti
                    isClearable={false}
                    value={negativeKeywords.map((keyword, index) => ({
                      value: index,
                      label: keyword
                    }))}
                    noOptionsMessage={() => 'Type a negative keyword'}
                    onChange={(selectedOption) => {
                      setNegativeKeywords(
                        selectedOption
                          ? selectedOption.map((keyword) => keyword.label)
                          : []
                      );
                    }}
                    options={[]}
                    placeholder="Type a negative keyword"
                    styles={customMultiSelectorStyle}
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    Additional Services
                  </Label>
                  <CreatableSelect
                    id="additionalServices"
                    name="additionalServices"
                    isMulti
                    isClearable={false}
                    value={additionalServices.map((service, index) => ({
                      value: index,
                      label: service
                    }))}
                    noOptionsMessage={() => 'Type an additional service'}
                    onChange={(selectedOption) => {
                      setAdditionalServices(
                        selectedOption
                          ? selectedOption.map((service) => service.label)
                          : []
                      );
                    }}
                    options={[]}
                    placeholder="Type an additional service"
                    styles={customMultiSelectorStyle}
                  />
                </div>
              </div>
              {/* Holidays Section */}
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    Holidays
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Select holidays to create special content for
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2">
                  {getHolidaysFromEnv().map((holiday) => (
                    <div key={holiday} className="flex items-center space-x-3">
                      <Checkbox
                        id={`holiday-${holiday}`}
                        checked={holidays.includes(holiday)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setHolidays((prev) => [...prev, holiday]);
                          } else {
                            setHolidays((prev) =>
                              prev.filter((h) => h !== holiday)
                            );
                          }
                        }}
                        className="h-5 w-5 rounded border-2 border-primary data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <label
                        htmlFor={`holiday-${holiday}`}
                        className="text-base cursor-pointer">
                        {holiday}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Reviews Settings Card */}
          <Card className="shadow-md rounded-2xl border-0">
            <CardHeader className="pb-4 pt-6 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <ToggleLeft className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl lg:text-2xl font-bold">
                    Reviews Settings
                  </CardTitle>
                  <CardDescription className="text-base mt-1">
                    Control review management features for this account
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 px-4 sm:px-6 lg:px-8 pb-8">
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">
                    Automatic Reviews
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Enable automatic review requests
                  </p>
                </div>
                <Switch
                  checked={automaticReviews}
                  onCheckedChange={setAutomaticReviews}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
              <div className="flex items-center justify-between py-4 border-b border-border">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">
                    Single Person Business
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Is this a single person business?
                  </p>
                </div>
                <Switch
                  checked={singlePersonBusiness}
                  onCheckedChange={setSinglePersonBusiness}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
              <div className="flex items-center justify-between py-4">
                <div className="space-y-1">
                  <Label className="text-base font-semibold">No Reviews</Label>
                  <p className="text-sm text-muted-foreground">
                    Disable reviews for this client
                  </p>
                </div>
                <Switch
                  checked={noReviews}
                  onCheckedChange={setNoReviews}
                  className="data-[state=checked]:bg-primary scale-75"
                />
              </div>
            </CardContent>
          </Card>
          <div className="pt-4 pb-8 flex justify-end">
            <button
              className="bg-primary hover:bg-primary/90 text-primary-foreground h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3 flex items-center"
              onClick={handleSaveClient}
              disabled={loading || uploadingLogo}>
              <Save className="h-5 w-5" />
              {uploadingLogo
                ? 'Uploading Logo...'
                : loading
                ? 'Saving...'
                : 'Save Client'}
            </button>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      <Dialog open={successModalOpen} onOpenChange={setSuccessModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              The client was created successfully.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => {
                setSuccessModalOpen(false);
                history.push('/clients');
              }}>
              OK
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewClient;
